import * as React from "react";
import {useEffect, useState} from "react";
import {Banknote, Check, X} from "lucide-react";

// Cash step of the payment modal - Vino POS's CashTenderedModal in the Vara
// theme. With "No change due" ticked, whatever was tendered over the amount
// due is the customer's tip.
interface CashTenderedModalProps {
    isOpen: boolean;
    onClose: () => void;
    amountDue: number;
    onConfirm: (tenderedAmount: number, changeGiven: number, tipAmount: number) => void;
}

const CashTenderedModal: React.FC<CashTenderedModalProps> = ({isOpen, onClose, amountDue, onConfirm}) => {
    const [tenderedInput, setTenderedInput] = useState("");
    const [noChangeDue, setNoChangeDue] = useState(false);
    const [changeDue, setChangeDue] = useState(0);

    useEffect(() => {
        const tendered = parseFloat(tenderedInput) || 0;
        setChangeDue(tendered - amountDue);
    }, [tenderedInput, amountDue]);

    useEffect(() => {
        if (isOpen) {
            setTenderedInput("");
            setNoChangeDue(false);
        }
    }, [isOpen]);

    const handleQuickAmount = (amount: number) => {
        setTenderedInput((prev) => {
            const current = parseFloat(prev) || 0;
            return (current + amount).toFixed(2);
        });
    };

    const handleExact = () => {
        setTenderedInput(amountDue.toFixed(2));
    };

    const handleClear = () => {
        setTenderedInput("");
    };

    const handleConfirmClick = () => {
        const tendered = parseFloat(tenderedInput) || amountDue;
        const tip = noChangeDue && tendered > amountDue ? tendered - amountDue : 0;
        const change = noChangeDue ? 0 : Math.max(0, changeDue);
        onConfirm(tendered, change, tip);
    };

    if (!isOpen) return null;

    return (
        <div className="absolute inset-0 z-50 flex items-center justify-center rounded-lg bg-black bg-opacity-50">
            <div className="mx-4 max-h-full w-full max-w-sm overflow-y-auto rounded-xl border border-neutral-100 bg-default-background shadow-xl">
                {/* Header */}
                <div className="flex items-start justify-between border-b border-neutral-200 p-4">
                    <div className="flex gap-2">
                        <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-success-50 text-success-600">
                            <Banknote className="h-5 w-5"/>
                        </div>
                        <div>
                            <h3 className="text-base font-bold leading-tight text-neutral-900">Cash Payment</h3>
                            <p className="mt-0.5 text-xs text-neutral-500">
                                Amount Due:{" "}
                                <span className="font-bold text-neutral-900">${amountDue.toFixed(2)}</span>
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="rounded-full p-1 transition-colors hover:bg-neutral-100">
                        <X className="h-5 w-5 text-neutral-400"/>
                    </button>
                </div>

                {/* Content */}
                <div className="space-y-4 p-4">
                    {/* Cash Tendered Input */}
                    <div>
                        <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-neutral-700">
                            Cash Tendered
                        </label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-neutral-500">$</span>
                            <input
                                type="number"
                                value={tenderedInput}
                                onChange={(e) => setTenderedInput(e.target.value)}
                                placeholder="0.00"
                                min="0"
                                step="0.01"
                                autoFocus
                                className="w-full rounded-lg border border-neutral-300 py-2.5 pl-8 pr-4 text-base font-semibold shadow-sm focus:border-neutral-900 focus:outline-none focus:ring-2 focus:ring-brand-primary"
                            />
                        </div>
                    </div>

                    {/* Quick Cash Grid */}
                    <div>
                        <span className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-neutral-400">
                            Quick Cash
                        </span>
                        <div className="space-y-2">
                            <div className="grid grid-cols-3 gap-2">
                                {[1, 5, 10, 20, 50, 100].map((amt) => (
                                    <button
                                        key={amt}
                                        onClick={() => handleQuickAmount(amt)}
                                        className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-center text-xs font-semibold text-neutral-700 transition-colors hover:border-neutral-400 hover:bg-brand-50"
                                    >
                                        ${amt}
                                    </button>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleExact}
                                    className="flex-1 rounded-lg border border-success-200 bg-white px-4 py-2 text-center text-xs font-semibold text-success-700 transition-colors hover:bg-success-50"
                                >
                                    Exact
                                </button>
                                <button
                                    onClick={handleClear}
                                    className="flex-1 rounded-lg border border-error-200 bg-white px-4 py-2 text-center text-xs font-semibold text-error-600 transition-colors hover:bg-error-50"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* No Change Due checkbox */}
                    <div>
                        <button
                            onClick={() => setNoChangeDue(!noChangeDue)}
                            className="flex items-center gap-2.5 rounded p-1 text-left transition-colors"
                        >
                            <div
                                className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors ${
                                    noChangeDue ? "border-neutral-900 bg-neutral-900" : "border-neutral-300 bg-white"
                                }`}
                            >
                                {noChangeDue && <Check className="h-3 w-3 text-brand-primary"/>}
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xs font-bold text-neutral-700">No change due</span>
                                <span className="text-[10px] text-neutral-400">Exact change or customer keeps difference</span>
                            </div>
                        </button>
                    </div>

                    {/* Shortfall or Change Due Card */}
                    <div
                        className={`space-y-2 rounded-xl border p-4 text-xs ${
                            noChangeDue
                                ? "border-neutral-200 bg-neutral-50"
                                : changeDue >= 0
                                    ? "border-success-100 bg-success-50 text-success-800"
                                    : "border-error-100 bg-error-50 text-error-800"
                        }`}
                    >
                        <div className="flex justify-between font-medium text-neutral-500">
                            <span>Amount Due:</span>
                            <span className="font-semibold text-neutral-900">${amountDue.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-medium text-neutral-500">
                            <span>Cash Tendered:</span>
                            <span className="font-semibold text-neutral-900">${(parseFloat(tenderedInput) || 0).toFixed(2)}</span>
                        </div>
                        {noChangeDue && changeDue > 0 ? (
                            <>
                                <div className="flex justify-between font-medium text-success-700">
                                    <span>Tip (Keep Change):</span>
                                    <span className="font-bold text-success-800">+${changeDue.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between border-t border-dashed border-neutral-300 pt-2 text-sm font-bold text-neutral-900">
                                    <span>Change Due:</span>
                                    <span>$0.00</span>
                                </div>
                            </>
                        ) : (
                            <div
                                className={`flex justify-between border-t border-dashed pt-2 text-sm font-bold ${
                                    noChangeDue
                                        ? "border-neutral-300 text-neutral-900"
                                        : changeDue >= 0
                                            ? "border-success-200 text-success-700"
                                            : "border-error-200 text-error-600"
                                }`}
                            >
                                <span>{noChangeDue ? "Change Due:" : changeDue >= 0 ? "Change Due:" : "Shortfall:"}</span>
                                <span>${noChangeDue ? "0.00" : Math.abs(changeDue).toFixed(2)}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex gap-3 border-t border-neutral-200 bg-neutral-50 p-4">
                    <button
                        onClick={onClose}
                        className="flex-1 rounded-lg bg-neutral-100 px-4 py-2.5 text-center text-xs font-semibold text-neutral-600 transition-colors hover:bg-neutral-200"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirmClick}
                        disabled={!noChangeDue && changeDue < 0 && (parseFloat(tenderedInput) || 0) < amountDue}
                        className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-neutral-900 px-4 py-2.5 text-xs font-semibold text-white shadow transition-colors hover:bg-neutral-700 disabled:bg-neutral-200 disabled:text-neutral-400"
                    >
                        <Banknote className="h-4 w-4"/>
                        Complete Sale
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CashTenderedModal;
